import { Component, OnInit, AfterViewInit, Inject, PLATFORM_ID, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { EventService, Event } from "../services/event.service";
import { UserService } from "../core/services/user.service";
import { LanguageService } from "../core/services/language.service";
import { getUserDetails } from "../shared/utils/storage-utils";
import { pageLoadAnimation } from "../shared/utils/common-animations";
import { Subscription } from "rxjs";

@Component({
  selector: "event-details-component, EventDetailsComponent",
  standalone: true,
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimation]
})
export class EventDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  pageLoadState = 'hidden';
  event: Event | null = null;
  isLoading = true;
  hasError = false;
  isRegistering = false;
  registrationError = '';
  activitiesSelected = false;
  imagesLoaded: { [key: string]: boolean } = {};
  private readonly imageUrlCache = new Map<string, string>();
  preloadedImageUrls = new Map<string, string>();
  private languageSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly eventService: EventService,
    private readonly userService: UserService,
    private readonly languageService: LanguageService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    const state = (history.state as { activitiesSelected?: boolean }) || {};
    this.activitiesSelected = state.activitiesSelected ?? false;

    const selectedEventId = this.eventService.getSelectedEvent();
    if (selectedEventId) {
      this.loadEventDetails(selectedEventId);
    } else {
      this.router.navigate(['/upcoming-events'], {
        state: { activitiesSelected: this.activitiesSelected },
      });
    }
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  private getUserId(): number | null {
    let userId = this.userService.getStoredUserId();
    if (!userId) {
      const userDetails = getUserDetails();
      userId = userDetails?.value ?? userDetails?.id ?? userDetails?.userId;
    }
    const parsed = Number(userId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private loadEventDetails(eventId: string): void {
    this.isLoading = true;
    this.hasError = false;
    this.registrationError = '';
    const userId = this.getUserId();
    this.eventService.getEventById(eventId, userId ?? undefined).subscribe({
      next: (event) => {
        if (event) {
          this.event = event;
          this.isLoading = false;

          if (isPlatformBrowser(this.platformId)) {
            this.preloadEventImages();
          }
        } else {
          this.hasError = true;
          this.isLoading = false;
        }
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
        this.event = null;
      }
    });
  }

  private preloadEventImages(): void {
    if (!this.event) return;

    const images = [
      { id: 'main', url: this.event.image },
      { id: 'map', url: this.event.mapImage }
    ].filter((img): img is { id: string; url: string } => !!img.url);

    images.forEach(({ id, url }) => {
      this.imageUrlCache.set(id, url);
      this.preloadedImageUrls.set(id, url);

      const preloadImg = new Image();
      preloadImg.fetchPriority = 'high';
      preloadImg.loading = 'eager';
      preloadImg.decoding = 'sync';

      preloadImg.src = url;

      if (preloadImg.complete && preloadImg.naturalWidth > 0 && preloadImg.naturalHeight > 0) {
        this.imagesLoaded[id] = true;
        this.cdr.detectChanges();
        return;
      }

      preloadImg.onload = () => {
        if (preloadImg.complete && preloadImg.naturalWidth > 0 && preloadImg.naturalHeight > 0) {
          this.imagesLoaded[id] = true;
          this.cdr.detectChanges();
        }
      };

      preloadImg.onerror = () => {
        this.imagesLoaded[id] = false;
      };
    });
  }

  isImageLoaded(imageId: string): boolean {
    return !!this.imagesLoaded[imageId];
  }

  getImageUrl(imageId: string): string {
    return this.preloadedImageUrls.get(imageId) ?? this.imageUrlCache.get(imageId) ?? '';
  }

  loadEvent(): void {
    const selectedEventId = this.eventService.getSelectedEvent();
    if (selectedEventId) {
      this.loadEventDetails(selectedEventId);
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
  }

  onBackClick(): void {
    this.router.navigate(['/upcoming-events'], {
      state: { activitiesSelected: this.activitiesSelected },
    });
  }

  get isRegistered(): boolean {
    return this.event?.isRegistered === true;
  }

  get isWaitlisted(): boolean {
    return this.event?.isWaitlisted === true;
  }

  get hasActiveRegistration(): boolean {
    return this.isRegistered || this.isWaitlisted;
  }

  get registerButtonLabel(): string {
    if (this.isRegistering) {
      if (this.hasActiveRegistration) {
        return this.languageService.t('events.cancelRegistration') + '...';
      }
      return this.languageService.t('events.register') + '...';
    }
    if (this.hasActiveRegistration) {
      return this.languageService.t('events.cancelRegistration');
    }
    return this.languageService.t('events.register');
  }

  get confirmedLabel(): string {
    return this.languageService.t('events.confirmed');
  }

  get waitingListLabel(): string {
    const count = this.event?.waitlistedCount ?? 0;
    return this.languageService.t('events.waitingListCount', { count });
  }

  get whereLabel(): string {
    return this.languageService.t('common.where');
  }

  get whenLabel(): string {
    return this.languageService.t('common.when');
  }

  get centerLabel(): string {
    return this.languageService.t('common.center');
  }

  get backLabel(): string {
    return this.languageService.t('common.back');
  }

  onRegisterClick(): void {
    if (!this.event || this.isRegistering) {
      return;
    }

    const userId = this.getUserId();
    if (!userId) {
      this.registrationError = 'Unable to identify the senior. Please restart check-in.';
      return;
    }

    this.isRegistering = true;
    this.registrationError = '';
    const wasActive = this.hasActiveRegistration;

    const request$ = wasActive
      ? this.eventService.unregisterFromEvent(this.event.id, userId)
      : this.eventService.registerForEvent(this.event.id, userId);

    request$.subscribe({
      next: (response) => {
        this.isRegistering = false;
        if (response?.statusCode === 200) {
          if (wasActive) {
            this.router.navigate(['/upcoming-events'], {
              state: { activitiesSelected: this.activitiesSelected },
            });
            return;
          }

          // Confirmed and waitlisted registrations both go to the success/done page.
          this.router.navigate(['/success'], {
            state: { activitiesSelected: this.activitiesSelected },
          });
        } else {
          this.registrationError = wasActive
            ? 'Unable to cancel registration. Please try again.'
            : 'Unable to register for this event. Please try again.';
        }
      },
      error: () => {
        this.isRegistering = false;
        this.registrationError = wasActive
          ? 'Unable to cancel registration. Please try again.'
          : 'Unable to register for this event. Please try again.';
      }
    });
  }

  getGoogleMapsUrl(address: string): string {
    return `https://maps.google.com/?q=${encodeURIComponent(address)}`;
  }
}
